import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { useSignIn, useAuth } from "@clerk/clerk-react"
import { useNavigate, Link } from "react-router-dom"
import { motion, useAnimationControls } from "framer-motion"
import { ThemeToggle } from "../../components/ThemeToggle"
import { useBrand } from "../../theme/useBrand"

type PhoneInputEvent = React.ChangeEvent<HTMLInputElement>
type FormSubmitEvent = React.FormEvent<HTMLFormElement>

const GLYPHS = "!<>-_\\/#$%&=+*?@[]{}~"
const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? "*"

function useGlyphAnimator(text: string, options?: { scrambleInterval?: number }) {
  const { scrambleInterval = 38 } = options ?? {}
  const characters = useMemo(() => text.split(""), [text])
  const computeState = useCallback((mode: "blank" | "final") =>
    characters.map(char => (char === " " ? " " : mode === "final" ? char : "")), [characters])
  const [display, setDisplay] = useState<string[]>(() => computeState("final"))
  const timeoutsRef = useRef<number[]>([])
  const intervalsRef = useRef<Array<number | null>>([])

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach(id => window.clearTimeout(id))
    intervalsRef.current.forEach(id => id !== null && window.clearInterval(id))
    timeoutsRef.current = []
    intervalsRef.current = []
  }, [])

  const setCharacter = useCallback((index: number, value: string) => {
    setDisplay(prev => { const next = [...prev]; next[index] = value; return next })
  }, [])

  const startLoop = useCallback(() => {
    clearTimers()
    intervalsRef.current = characters.map(() => null)
    characters.forEach((char, index) => {
      if (char === " ") { setCharacter(index, " "); return }
      intervalsRef.current[index] = window.setInterval(() => setCharacter(index, randomGlyph()), scrambleInterval)
    })
  }, [characters, clearTimers, scrambleInterval, setCharacter])

  const stopLoop = useCallback(() => { clearTimers(); setDisplay(computeState("final")) }, [clearTimers, computeState])

  useEffect(() => clearTimers, [clearTimers])
  return { characters, display, startLoop, stopLoop }
}

function GlyphDisplay({ characters, display, prefix }: { characters: string[]; display: string[]; prefix: string }) {
  return <>{characters.map((_, i) => <span key={`${prefix}-${i}`} className="glitch-typewriter-char">{display[i] || "\u00a0"}</span>)}</>
}

type ClerkError = {
  message?: string
  errors?: Array<{ code?: string; message?: string }>
}

function isAlreadySignedInError(error: unknown): boolean {
  const clerkError = error as ClerkError | null | undefined
  const code = (clerkError?.errors?.[0]?.code || "").toLowerCase()
  const message = (clerkError?.errors?.[0]?.message || clerkError?.message || "").toLowerCase()
  return (
    code === "session_exists"
    || code === "already_signed_in"
    || message.includes("already signed in")
  )
}

function normalizePhone(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ""
  const digitsOnly = trimmed.replace(/[^0-9+]/g, "")
  if (!digitsOnly) return ""
  if (digitsOnly.startsWith("+")) return digitsOnly
  const justDigits = digitsOnly.replace(/[^0-9]/g, "")
  return justDigits ? `+${justDigits}` : ""
}

export default function LoginPage() {
  const { signIn, setActive, isLoaded } = useSignIn()
  const { isSignedIn } = useAuth()
  const navigate = useNavigate()
  const controls = useAnimationControls()
  const brand = useBrand()

  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [step, setStep] = useState<"phone" | "code">("phone")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showContent, setShowContent] = useState(false)

  const entryHeading = brand.isFairy ? "WELCOME" : "SUMMON"
  const entryPronunciation = brand.isFairy ? "/ˈwel.kəm/" : "/ˈsʌm.ən/"
  const entryTagline = brand.isFairy
    ? "Return to your enchanted back office."
    : "Enter the portal to your back office."

  const { characters: enterChars, display: enterDisplay, startLoop: startEnter, stopLoop: stopEnter } =
    useGlyphAnimator("Enter", { scrambleInterval: 28 })

  const { characters: verifyChars, display: verifyDisplay, startLoop: startVerify, stopLoop: stopVerify } =
    useGlyphAnimator("Verify", { scrambleInterval: 28 })

  useEffect(() => {
    if (isSignedIn) navigate("/app", { replace: true })
  }, [isSignedIn, navigate])

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 400)
    return () => clearTimeout(timer)
  }, [])

  const handleSendCode = useCallback(async (e: FormSubmitEvent) => {
    e.preventDefault()
    if (!isLoaded || !signIn) return
    const normalized = normalizePhone(phone)
    if (!normalized || normalized.length < 10) { setError("Enter a valid phone number"); return }
    setLoading(true)
    setError(null)
    try {
      const result = await signIn.create({ identifier: normalized })
      const phoneFactor = result.supportedFirstFactors?.find(f => f.strategy === "phone_code")
      if (phoneFactor && "phoneNumberId" in phoneFactor) {
        await signIn.prepareFirstFactor({ strategy: "phone_code", phoneNumberId: phoneFactor.phoneNumberId })
        setStep("code")
        controls.start({ scale: [1, 1.02, 1], transition: { duration: 0.4 } })
      } else {
        setError("Phone verification not available. Try signing up first.")
      }
    } catch (err: unknown) {
      // Clerk rejects signIn.create with session_exists when this browser
      // already holds an active session; the only way forward is into the app.
      if (isAlreadySignedInError(err)) {
        navigate("/app", { replace: true })
        return
      }
      const message = err instanceof Error ? err.message : (err as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message || "Failed to send code"
      setError(message)
    } finally { setLoading(false) }
  }, [isLoaded, signIn, phone, controls, navigate])

  const handleVerifyCode = useCallback(async (e: FormSubmitEvent) => {
    e.preventDefault()
    if (!isLoaded || !signIn) return
    const cleanCode = code.trim()
    if (!cleanCode || cleanCode.length < 4) { setError("Enter the verification code"); return }
    setLoading(true)
    setError(null)
    try {
      const result = await signIn.attemptFirstFactor({ strategy: "phone_code", code: cleanCode })
      if (result.status === "complete") {
        await setActive?.({ session: result.createdSessionId })
        navigate("/app")
      } else { setError("Verification incomplete. Please try again.") }
    } catch (err: unknown) {
      if (isAlreadySignedInError(err)) {
        navigate("/app", { replace: true })
        return
      }
      const message = err instanceof Error ? err.message : (err as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message || "Invalid code"
      setError(message)
    } finally { setLoading(false) }
  }, [isLoaded, signIn, code, setActive, navigate])

  return (
    <>
      <div className="rail-top" />
      <div className="rail-bottom" />

      <div style={{ position: "fixed", top: "16px", right: "16px", zIndex: 20 }}>
        <ThemeToggle variant="compact" />
      </div>

      <main className="container-hero">
        <div className="hero-frame">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ alignItems: "center", textAlign: "center" }}
          >
            <div className="sprite-sigil" style={{ marginBottom: "16px" }}>
              <img src={brand.sprite} width={24} height={24} className="pixel" alt={`${brand.name} logo`} />
            </div>

            <h1 className="brand-xl" style={{ marginBottom: "12px" }}>
              {step === "phone" ? entryHeading : "VERIFY"}
            </h1>

            <div className="brand-small" style={{ marginBottom: "32px" }}>
              {step === "phone" ? entryPronunciation : "/ˈver.ɪ.faɪ/"}
            </div>

            <p className="hero-tagline text-muted" style={{ textAlign: "center", maxWidth: "400px", marginBottom: "32px" }}>
              {step === "phone" 
                ? entryTagline
                : `Code sent to ${phone}`}
            </p>

            <motion.div
              animate={controls}
              style={{ width: "100%", maxWidth: "400px" }}
            >
              {step === "phone" ? (
                <form onSubmit={handleSendCode} style={{ display: "grid", gap: "24px" }}>
                  <input
                    id="phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e: PhoneInputEvent) => setPhone(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="input-mono"
                    style={{
                      width: "100%",
                      height: "64px",
                      fontSize: "24px",
                      textAlign: "center",
                      letterSpacing: "0.08em",
                    }}
                  />

                  {error && (
                    <p className="mono" style={{ color: "var(--error-text)", fontSize: "12px" }}>{error}</p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading || !phone.trim()}
                    className="btn-lg btn-primary mono"
                    onPointerEnter={startEnter}
                    onPointerLeave={stopEnter}
                    whileHover={{ scale: 1.04, boxShadow: "0 0 28px rgba(255, 43, 79, 0.5)" }}
                    whileTap={{ scale: 0.96 }}
                    style={{
                      width: "100%",
                      padding: "18px",
                      opacity: loading || !phone.trim() ? 0.6 : 1,
                      cursor: loading || !phone.trim() ? "not-allowed" : "pointer",
                    }}
                  >
                    <span className="glitch-typewriter">
                      {loading ? "Sending..." : <GlyphDisplay characters={enterChars} display={enterDisplay} prefix="enter" />}
                    </span>
                  </motion.button>
                </form>
              ) : (
                <form onSubmit={handleVerifyCode} style={{ display: "grid", gap: "24px" }}>
                  <input
                    id="code-input"
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e: PhoneInputEvent) => setCode(e.target.value)}
                    placeholder="000000"
                    className="input-mono"
                    maxLength={6}
                    style={{
                      width: "100%",
                      height: "64px",
                      fontSize: "32px",
                      textAlign: "center",
                      letterSpacing: "0.4em",
                    }}
                  />

                  {error && (
                    <p className="mono" style={{ color: "var(--error-text)", fontSize: "12px" }}>{error}</p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading || !code.trim()}
                    className="btn-lg btn-primary mono"
                    onPointerEnter={startVerify}
                    onPointerLeave={stopVerify}
                    whileHover={{ scale: 1.04, boxShadow: "0 0 28px rgba(255, 43, 79, 0.5)" }}
                    whileTap={{ scale: 0.96 }}
                    style={{
                      width: "100%",
                      padding: "18px",
                      opacity: loading || !code.trim() ? 0.6 : 1,
                      cursor: loading || !code.trim() ? "not-allowed" : "pointer",
                    }}
                  >
                    <span className="glitch-typewriter">
                      {loading ? "Verifying..." : <GlyphDisplay characters={verifyChars} display={verifyDisplay} prefix="verify" />}
                    </span>
                  </motion.button>

                  <button
                    type="button"
                    onClick={() => { setStep("phone"); setCode(""); setError(null) }}
                    className="mono"
                    style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "12px", letterSpacing: "0.1em", cursor: "pointer" }}
                  >
                    ← Use different number
                  </button>
                </form>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{ delay: 0.6 }}
              style={{ marginTop: "32px" }}
            >
              <p className="mono" style={{ color: "var(--muted)", fontSize: "12px", letterSpacing: "0.08em" }}>
                Don&apos;t have a {brand.isFairy ? "fairy" : "demon"}?{" "}
                <Link to="/onboard/intro" style={{ color: "var(--accent-text)" }}>
                  {brand.isFairy ? "Invite one" : "Summon one"}
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </main>

      {/* Bottom-left steps nav */}
      <motion.div
        className="steps"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <span className="num" style={{ color: step === "phone" ? "var(--accent)" : "var(--muted)" }}>01</span>
        <span className="lbl" style={{ color: step === "phone" ? "var(--text)" : "var(--muted)" }}>Enter Number</span>
        <span className="num" style={{ color: step === "code" ? "var(--accent)" : "var(--muted)" }}>02</span>
        <span className="lbl" style={{ color: step === "code" ? "var(--text)" : "var(--muted)" }}>Verify Code</span>
        <span className="num">03</span>
        <span className="lbl">Access Granted</span>
      </motion.div>
    </>
  )
}
